/* eslint-disable jsx-a11y/anchor-is-valid */
import { Box, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link } from 'react-router-dom';
import { AppRoutes } from '../../../../constants/appRoutes';
import { LoanStatuses } from '../../../../constants/loanStatus';
import { buildLocalizeRoute } from '../../../../helpers/languageHelper';
import { currencyFormatterHelper } from '../../../../helpers/currencyFormatterHelper';
import { DateHelper } from '../../../../helpers/dateHelper';
import { getRiskRatingKey } from '../../../../helpers/riskRatingHelper';
import { LabelBadge } from '../../../../shared/components/common/labelBadge';
import RatingBadge from '../../../../pages/investor/components/ratingBadge';
import { ButtonWrapper } from '../../../../shared/components/formUtilities/useButton';

const statusColorMap = {
	OnTime: { bg: '#ffffff', text: '#063236', outlineClass: 'outline outline-blue-300' },
	Late: { bg: '#ffffff', text: '#063236', outlineClass: 'outline outline-blue-300' },
	Default: { bg: '#EC9697', text: '#063236' },
	PaidLate: { bg: '#00D175', text: '#063236' },
	Paid: { bg: '#00D175', text: '#063236' },
	FullyPaid: { bg: '#00D175', text: '#063236' },
};

const statusTextMapping = {
	OnTime: 'On Time',
	Late: 'Late',
	Default: 'Default',
	PaidLate: 'Paid Late',
	Paid: 'Paid',
	FullyPaid: 'Fully Paid',
};


export const InitializeMyordersColumns = (t, onCancelInvestment) => {
	return [
		{
			header: t('businessName'),
			accessorKey: 'businessName',
			maxSize: 80,
		},
		{
			header: t('industry'),
			accessorKey: 'businessIndustry',
			maxSize: 100,
		},
		{
			header: t('amount'),
			accessorKey: 'amount',
			Cell: ({ cell }) => {
				return currencyFormatterHelper(cell?.getValue());
			},
			maxSize: 100,
		},
		{
			header: t('riskRating'),
			accessorKey: 'rating',
			Cell: ({ cell }) => <RatingBadge rating={getRiskRatingKey(cell?.getValue())} />,
			maxSize: 100,
		},
		{
			header: t('type'),
			accessorKey: 'paymentFrequency',
			Cell: ({ cell }) => {
				let value = cell?.getValue();
				if (value === 0) {
					return 'Monthly';
				}
				if (value === 1) {
					return 'OneOff';
				}
			},
			maxSize: 100,
		},
		{
			header: t('profitRate'),
			accessorKey: 'investorProfit',
			Cell: ({ cell }) => {
				return `${cell?.getValue()}%`;
			},
			maxSize: 100,
		},
		{
			header: t('action'),
			accessorKey: 'actions',
			Cell: ({ cell, row }) => {
				return <Box>
					{row?.original?.loanStatus === LoanStatuses.Fundraising ?
						<ButtonWrapper labelClass='!text-[0.875rem]' classList='!py-0.5 !px-2 !border-investor-divider !text-investor-deep' variant={'outlined'} onClick={() => {
							onCancelInvestment && onCancelInvestment(row);
						}} label={t('cancelOrder')} /> : null}
				</Box>;
			},
			maxSize: 100,
		}
	];
};

export const InitializeRatiosColumns = (t, onDownloadRepaymentSchedule, downloadingLoanIds) => {
	return [
		{
			header: t('businessName'),
			accessorKey: 'title',
			// Links to the business history details page so investors can review the
			// loan's payments straight from Current Investments (same target as the
			// Portfolio Updates links). Falls back to plain text when the row has no
			// loan id, so a missing id never renders a broken link.
			Cell: ({ cell, row }) => {
				const loanId = row?.original?.loanId;
				const name = cell?.getValue();
				return loanId ? (
					// A global `a {}` rule (template.scss) forces font-size:1rem and a
					// primary color on every anchor; inherit color/size/line-height so the
					// link looks identical to the original plain-text cell (black, bold).
					<Link
						className='font-bold hover:underline'
						style={{
							color: 'inherit',
							fontSize: 'inherit',
							lineHeight: 'inherit',
							textAlign: 'inherit',
						}}
						to={buildLocalizeRoute(
							`${AppRoutes.Investor}/${AppRoutes.Marketplace}/${AppRoutes.MarketplaceBusinessHistory}/${loanId}`
						)}>
						{name}
					</Link>
				) : (
					<span className='font-bold'>{name}</span>
				);
			},
			maxSize: 160,
		},
		{
			header: t('industry'),
			accessorKey: 'businessIndustry',
			size: 50,
		},
		{
			header: t('risk'),
			accessorKey: 'risk',
			Cell: ({ cell }) => <RatingBadge rating={cell?.getValue()} />,
			size: 50,
		},
		{
			header: t('tenure'),
			accessorKey: 'tenure',
			size: 30,
		},
		{
			header: t('repaymentsLeft'),
			accessorKey: 'repaymentsLeft',
			size: 120,
		},
		{
			header: t('investedAmount'),
			accessorKey: 'totalInvestment',
			size: 120,
			Cell: ({ cell }) => {
				return currencyFormatterHelper(cell?.getValue());
			},
		},
		{
			header: t('amountOutstanding'),
			accessorKey: 'principalRemaining',
			Cell: ({ cell }) => {
				return currencyFormatterHelper(cell?.getValue());
			},
			size: 140,
		},
		{
			header: t('nextPayment'),
			accessorKey: 'nextPaymentAmount',
			Cell: ({ cell }) => {
				return currencyFormatterHelper(cell?.getValue());
			},
			size: 120,
		},
		{
			header: t('nextPaymentDue'),
			accessorKey: 'nextPayment',
			Cell: ({ cell }) => {
				return DateHelper.formatDate(cell?.getValue(), 'dd/MM/yyyy');
			},
			size: 130,
		},
		{
			header: t('profitRate'),
			accessorKey: 'investorProfit',
			Cell: ({ cell }) => {
				return `${cell?.getValue()}%`;
			},
			size: 50,
		},
		{
			header: t('status'),
			accessorKey: 'loanStatus',
			Cell: ({ cell }) => {
				const raw = cell?.getValue();
				const displayStatus = statusTextMapping[raw] ?? raw;
				const colors = statusColorMap[raw] ?? { bg: 'rgba(150,150,150,0.15)', text: '#6e8192' };
				return <LabelBadge label={displayStatus} bgColor={colors.bg} textColor={colors.text} classList={`!rounded-full !px-2 !py-1 !text-[11px] !font-bold !normal-case !whitespace-nowrap ${colors.outlineClass ?? ''}`} />;
			},
			size: 80,
		},
		{
			header: t('agreement'),
			accessorKey: 'agreementDocumentUrl',
			Cell: ({ cell }) => {
				return cell?.getValue() ? <a target='_blank' href={cell?.getValue()} className='flex justify-center text-center underline-link' rel="noreferrer">
					{t('viewFile')}
				</a> : null;
			},
			size: 50,
		},
		{
			header: t('paymentHistory'),
			Header: () => (
				<Box className="flex items-center justify-center whitespace-nowrap">
					{t('paymentHistory')}
					{/* placement="top" keeps this above the header instead of the browser's native
					    title tooltip, which was landing on top of the row below it. */}
					<Tooltip title={t('paymentHistoryTooltip')} placement="top" arrow>
						<InfoOutlinedIcon className="!text-[16px] relative -top-[1px] mx-0.5 text-[#90ab9f]" />
					</Tooltip>
				</Box>
			),
			accessorKey: 'paymentHistory',
			Cell: ({ row }) => {
				const loanId = row?.original?.loanId;
				if (!loanId) {
					return null;
				}
				const isDownloading = downloadingLoanIds?.has(loanId);
				return (
					<a
						onClick={() => {
							if (!isDownloading) {
								onDownloadRepaymentSchedule && onDownloadRepaymentSchedule(loanId);
							}
						}}
						className={`flex justify-center text-center underline-link ${isDownloading ? 'opacity-50 pointer-events-none' : ''}`}>
						{t('download')}
					</a>
				);
			},
			size: 130,
		},
	];
};
